import '@mxa-argos/styles/global.css';
import '@mxa-argos/styles/sidebar-fix.css';
import '@mxa-argos/styles/leaflet.css';

import { AdminLayout } from '@mxa-argos/components/custom/admin-layout';
import { Toaster } from '@mxa-argos/components/shadcn/sonner';
import { DeveloperModeProvider } from '@mxa-argos/core/contexts/developer-mode';
import { resources } from '@mxa-argos/core/resources';
import { authProvider } from '@mxa-argos/providers/auth.provider';
import { ConfettiProvider } from '@mxa-argos/providers/confetti.provider';
import { dataProvider } from '@mxa-argos/providers/ops.provider';
import { ThemeProvider } from '@mxa-argos/providers/theme.provider';
import type { NotificationProvider } from '@refinedev/core';
import { Authenticated, Refine } from '@refinedev/core';
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router';
import { type ComponentType, lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';
import { toast } from 'sonner';

const LoginPage = lazy(() =>
  import('@mxa-argos/modules/auth/pages/login.page').then((m) => ({
    default: m.LoginPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@mxa-argos/modules/dashboard/pages/dashboard.page').then((m) => ({
    default: m.DashboardPage,
  })),
);
const DeviceListPage = lazy(() =>
  import('@mxa-argos/modules/devices/pages/device-list.page').then((m) => ({
    default: m.DeviceListPage,
  })),
);
const DeviceDetailsPage = lazy(() =>
  import('@mxa-argos/modules/devices/pages/device-details.page').then((m) => ({
    default: m.DeviceDetailsPage,
  })),
);
const OrderListPage = lazy(() =>
  import('@mxa-argos/modules/orders/pages/order-list.page').then((m) => ({
    default: m.OrderListPage,
  })),
);
const OrderDetailsPage = lazy(() =>
  import('@mxa-argos/modules/orders/pages/order-details.page').then((m) => ({
    default: m.OrderDetailsPage,
  })),
);
const OrderHoldDetailsPage = lazy(() =>
  import('@mxa-argos/modules/orders/pages/order-hold-details.page').then((m) => ({
    default: m.OrderHoldDetailsPage,
  })),
);
const FraudProfileListPage = lazy(() =>
  import('@mxa-argos/modules/fraud/pages/fraud-profile-list.page').then((m) => ({
    default: m.FraudProfileListPage,
  })),
);
const SimListPage = lazy(() =>
  import('@mxa-argos/modules/sims/pages/sim-list.page').then((m) => ({
    default: m.SimListPage,
  })),
);
const SimDetailsPage = lazy(() =>
  import('@mxa-argos/modules/sims/pages/sim-details.page').then((m) => ({
    default: m.SimDetailsPage,
  })),
);
const SubscriptionRedirectPage = lazy(() =>
  import('@mxa-argos/modules/subscriptions/pages/subscription-redirect.page').then((m) => ({
    default: m.SubscriptionRedirectPage,
  })),
);
const DetailedSubscriptionListPage = lazy(() =>
  import('@mxa-argos/modules/subscriptions/pages/detailed-subscription-list.page').then((m) => ({
    default: m.DetailedSubscriptionListPage,
  })),
);
const CancellationRedirectPage = lazy(() =>
  import('@mxa-argos/modules/subscriptions/pages/cancellation-redirect.page').then((m) => ({
    default: m.CancellationRedirectPage,
  })),
);
const CustomerListPage = lazy(() =>
  import('@mxa-argos/modules/customers/pages/customer-list.page').then((m) => ({
    default: m.CustomerListPage,
  })),
);
const CustomerDetailsPage = lazy(() =>
  import('@mxa-argos/modules/customers/pages/customer-details.page').then((m) => ({
    default: m.CustomerDetailsPage,
  })),
);
const PortabilityListPage = lazy(() =>
  import('@mxa-argos/modules/portabilities/pages/portability-list.page').then((m) => ({
    default: m.PortabilityListPage,
  })),
);
const PortabilityDetailsPage = lazy(() =>
  import('@mxa-argos/modules/portabilities/pages/portability-details.page').then((m) => ({
    default: m.PortabilityDetailsPage,
  })),
);
const ChangeListPage = lazy(() =>
  import('@mxa-argos/modules/changes/pages/change-list.page').then((m) => ({
    default: m.ChangeListPage,
  })),
);
const ChangeDetailsPage = lazy(() =>
  import('@mxa-argos/modules/changes/pages/change-details.page').then((m) => ({
    default: m.ChangeDetailsPage,
  })),
);
const ShipmentListPage = lazy(() =>
  import('@mxa-argos/modules/shipments/pages/shipment-list.page').then((m) => ({
    default: m.ShipmentListPage,
  })),
);
const ShipmentDetailsPage = lazy(() =>
  import('@mxa-argos/modules/shipments/pages/shipment-details.page').then((m) => ({
    default: m.ShipmentDetailsPage,
  })),
);
const ExternalAppListPage = lazy(() =>
  import('@mxa-argos/modules/external-apps/pages/external-app-list.page').then((m) => ({
    default: m.ExternalAppListPage,
  })),
);
const SupportPage = lazy(() =>
  import('@mxa-argos/modules/support/pages/support.page').then((m) => ({
    default: m.SupportPage,
  })),
);
const SyncPage = lazy(() =>
  import('@mxa-argos/modules/sync/pages/sync.page').then((m) => ({
    default: m.SyncPage,
  })),
);
const SearchPage = lazy(() =>
  import('@mxa-argos/modules/search/pages/search.page').then((m) => ({
    default: m.SearchPage,
  })),
);
const ForjaPage = lazy(() =>
  import('@mxa-argos/modules/forja/pages/forja.page').then((m) => ({
    default: m.ForjaPage,
  })),
);

const DevAgentation: ComponentType<{ endpoint?: string }> | null = import.meta.env.DEV
  ? lazy(() => import('agentation').then((m) => ({ default: m.Agentation })))
  : null;

const notificationProvider: NotificationProvider = {
  open: ({ message, description, type }) => {
    if (type === 'success') {
      toast.success(message, { description });
    } else if (type === 'error') {
      toast.error(message, { description, duration: Infinity });
    } else {
      toast.info(message, { description });
    }
  },
  close: (key) => {
    toast.dismiss(key);
  },
};

function App() {
  const { t, i18n } = useTranslation();

  const i18nProvider = useMemo(
    () => ({
      translate: (key: string, params: string) => t(key, params),
      changeLocale: (lang: string) => i18n.changeLanguage(lang),
      getLocale: () => i18n.language,
    }),
    [t, i18n],
  );

  return (
    <BrowserRouter>
      <ThemeProvider>
        <ConfettiProvider>
          <Toaster richColors closeButton position="top-right" />
          {DevAgentation && (
            <Suspense fallback={null}>
              <DevAgentation endpoint="http://localhost:4747" />
            </Suspense>
          )}
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            notificationProvider={notificationProvider}
            routerProvider={routerProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              projectId: 'wbkeYw-ETW48b-lefcNu',
              disableTelemetry: true,
            }}
            resources={resources}
            i18nProvider={i18nProvider}
          >
            <DeveloperModeProvider>
              <Routes>
                {/* Table pages - need h-full for internal table scroll */}
                <Route
                  element={
                    <Authenticated
                      key="authenticated-table-layout"
                      fallback={<CatchAllNavigate to="/login" />}
                    >
                      <AdminLayout variant="table">
                        <Suspense fallback={null}>
                          <Outlet />
                        </Suspense>
                      </AdminLayout>
                    </Authenticated>
                  }
                >
                  <Route path="/devices" element={<DeviceListPage />} />
                  <Route path="/orders" element={<OrderListPage />} />
                  <Route path="/fraud-profiles" element={<FraudProfileListPage />} />
                  <Route path="/sims" element={<SimListPage />} />
                  <Route path="/customers" element={<CustomerListPage />} />
                  <Route path="/portabilities" element={<PortabilityListPage />} />
                  <Route path="/changes" element={<ChangeListPage />} />
                  <Route path="/shipments" element={<ShipmentListPage />} />
                  <Route path="/subscriptions" element={<DetailedSubscriptionListPage />} />
                  <Route path="/external-apps" element={<ExternalAppListPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/forja" element={<ForjaPage />} />
                </Route>

                {/* Content pages - need overflow-y-auto for page scroll */}
                <Route
                  element={
                    <Authenticated
                      key="authenticated-content-layout"
                      fallback={<CatchAllNavigate to="/login" />}
                    >
                      <AdminLayout variant="content">
                        <Suspense fallback={null}>
                          <Outlet />
                        </Suspense>
                      </AdminLayout>
                    </Authenticated>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="/devices/:imei" element={<DeviceDetailsPage />} />
                  <Route path="/orders/holds/:id" element={<OrderHoldDetailsPage />} />
                  <Route path="/orders/:id" element={<OrderDetailsPage />} />
                  <Route path="/sims/:iccid" element={<SimDetailsPage />} />
                  <Route path="/customers/:id" element={<CustomerDetailsPage />} />
                  <Route path="/subscriptions/:id" element={<SubscriptionRedirectPage />} />
                  <Route path="/cancellations/:id" element={<CancellationRedirectPage />} />
                  <Route path="/portabilities/:id" element={<PortabilityDetailsPage />} />
                  <Route path="/changes/:id" element={<ChangeDetailsPage />} />
                  <Route path="/shipments/:id" element={<ShipmentDetailsPage />} />
                  <Route path="/sync" element={<SyncPage />} />
                  <Route path="/search" element={<SearchPage />} />
                </Route>

                <Route
                  element={
                    <Authenticated
                      key="auth-pages"
                      fallback={
                        <Suspense fallback={null}>
                          <Outlet />
                        </Suspense>
                      }
                    >
                      <NavigateToResource resource="dashboard" />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<LoginPage />} />
                </Route>
              </Routes>
            </DeveloperModeProvider>

            <UnsavedChangesNotifier />
            <DocumentTitleHandler
              handler={({ resource, action, autoGeneratedTitle }) => {
                const branch = import.meta.env.VITE_WORKTREE_BRANCH;
                const suffix = branch ? `Argos [${branch}]` : 'Argos';
                const labelKey = resource?.meta?.label;
                const resourceName = labelKey ? t(labelKey) : resource?.name;
                if (resourceName && action) {
                  return `${resourceName} | ${suffix}`;
                }
                return autoGeneratedTitle.replace('Refine', suffix);
              }}
            />
          </Refine>
        </ConfettiProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
